import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { LastMenstrualPeriod } from '@enums/last-menstrual-period';
import { environment } from '@environments/environment';
import { ConsultationStorageService } from '@services/consultation-storage.service';
import { NavigationService } from '@services/navigation.service';

@Injectable({
  providedIn: 'root',
})
export class RecentMenstruationRequiredGuard implements CanActivate {
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  private consultationStorageService = inject(ConsultationStorageService);

  /**
   * Determines whether the current route can be activated.
   *
   * @param {ActivatedRouteSnapshot} _route the route to be activated
   * @param {RouterStateSnapshot} state the current router state
   *
   * @returns {Promise<boolean | UrlTree>} a promise that resolves to true if the route can be activated, or a UrlTree
   * to redirect otherwise
   */
  public async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    if (this.isPostMenopausal()) {
      const nextPageUrl = await this.navigationService.getConsultationRequestNextPageUrl(
        this.consultationStorageService.treatmentType,
        state.url,
      );

      return this.router.createUrlTree([nextPageUrl]);
    }

    return true;
  }

  /**
   * Checks if the patient is post-menopausal or has no menstrual period.
   *
   * @returns {boolean} true if the patient is post-menopausal or has no menstrual period, false otherwise
   */
  private isPostMenopausal(): boolean {
    return (
      this.consultationStorageService.getQuestionnaireAnswers(this.consultationStorageService.treatmentType)?.[
        environment.consultation.lastPeriodQuestionId
      ] === LastMenstrualPeriod.PostMenopausal
    );
  }
}
